import api from './api';

export interface OrderItem {
  id?: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface Order {
  id?: number;
  reference: string;
  customerId: number;
  customerName: string;
  customerEmail: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED';
  paymentStatus: 'PENDING' | 'PAID' | 'REFUNDED' | 'FAILED';
  paymentMethod?: string;
  shippingAddress?: string;
  billingAddress?: string;
  notes?: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  shippingCost: number;
  totalAmount: number;
  items: OrderItem[];
  createdAt?: string;
  updatedAt?: string;
}

// Fonction pour mapper les données du backend vers le format frontend
const mapOrderFromBackend = (backendOrder: any): Order => {
  return {
    ...backendOrder,
    status: backendOrder.status?.toUpperCase() as Order['status'],
    paymentStatus: backendOrder.paymentStatus?.toUpperCase() as Order['paymentStatus']
  };
};

export const getOrders = async (options?: { mine?: boolean; status?: string; page?: number; limit?: number }): Promise<Order[]> => {
  try {
    const params: Record<string, any> = {};
    if (options?.mine) params.mine = 'true';
    if (options?.status) params.status = options.status;
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    console.log('Tentative de récupération des commandes depuis:', api.defaults.baseURL + '/orders', 'params:', params);
    
    // Vérifier si le token est présent
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Aucun token d\'authentification trouvé');
      window.location.href = '/login';
      return [];
    }
    
    const response = await api.get('/orders', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      params
    });
    
    console.log('Réponse reçue de l\'API - Statut:', response.status, 'Données:', response.data);
    
    if (!response.data) {
      console.error('Aucune donnée dans la réponse du serveur');
      throw new Error('Aucune donnée reçue du serveur');
    }
    
    // Extraire le tableau de commandes quel que soit le format de pagination retourné par l'API
    let rawOrders: any[] = [];
    if (Array.isArray(response.data?.data?.orders)) {
      rawOrders = response.data.data.orders;
    } else if (Array.isArray(response.data?.data)) {
      rawOrders = response.data.data;
    } else if (Array.isArray(response.data?.orders)) {
      rawOrders = response.data.orders;
    } else if (Array.isArray(response.data)) {
      rawOrders = response.data;
    }
    
    console.log('Nombre de commandes récupérées:', rawOrders.length);
    
    // Mapper les commandes au format frontend
    return rawOrders.map(mapOrderFromBackend);
    
  } catch (error: any) {
    console.error('Erreur détaillée lors de la récupération des commandes:', {
      message: error.message,
      code: error.code,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data
      } : 'Pas de réponse du serveur',
      request: error.request ? 'Requête effectuée mais pas de réponse' : 'Erreur avant l\'envoi de la requête'
    });
    
    let errorMessage = 'Impossible de charger les commandes. Veuillez réessayer plus tard.';
    
    if (error.response) {
      // La requête a été faite et le serveur a répondu avec un code d'erreur
      if (error.response.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        window.location.href = '/login';
        return [];
      } else if (error.response.status === 403) {
        errorMessage = 'Accès refusé : Vous n\'avez pas les droits nécessaires pour voir les commandes';
      } else if (error.response.status === 404) {
        errorMessage = 'Endpoint non trouvé. Vérifiez l\'URL de l\'API.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      // La requête a été faite mais aucune réponse n'a été reçue
      console.error('Aucune réponse du serveur. Vérifiez que le serveur est en cours d\'exécution.');
      errorMessage = 'Le serveur ne répond pas. Vérifiez votre connexion ou que le serveur est démarré.';
    } else if (error.code === 'ECONNREFUSED') {
      console.error('Connexion refusée. Le serveur est-il démarré?');
      errorMessage = 'Connexion impossible : Vérifiez que le serveur backend est démarré et accessible.';
    } else if (error.message) {
      // Erreur lors de la configuration de la requête
      console.error('Erreur de configuration de la requête:', error.message);
      errorMessage = `Erreur de configuration: ${error.message}`;
    }
    
    console.error('Message d\'erreur affiché à l\'utilisateur:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const deleteOrder = async (id: number): Promise<boolean> => {
  try {
    console.log(`Tentative de suppression de la commande ${id}`);
    
    // Vérifier si le token est présent
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Aucun token d\'authentification trouvé');
      window.location.href = '/login';
      return false;
    }
    
    const response = await api.delete(`/orders/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log(`Réponse de suppression de la commande ${id}:`, response.status, response.data);
    return true;
    
  } catch (error: any) {
    console.error(`Erreur détaillée lors de la suppression de la commande ${id}:`, {
      message: error.message,
      code: error.code,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      } : 'Pas de réponse du serveur'
    });
    
    let errorMessage = `Impossible de supprimer la commande ${id}. Veuillez réessayer.`;
    
    if (error.response) {
      // La requête a été faite et le serveur a répondu avec un code d'erreur
      if (error.response.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        window.location.href = '/login';
      } else if (error.response.status === 403) {
        errorMessage = 'Accès refusé : Vous n\'avez pas les droits nécessaires pour supprimer cette commande';
      } else if (error.response.status === 404) {
        errorMessage = `La commande ${id} n'existe pas ou a déjà été supprimée`;
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      // La requête a été faite mais aucune réponse n'a été reçue
      console.error('Aucune réponse du serveur lors de la suppression');
      errorMessage = 'Le serveur ne répond pas. Vérifiez votre connexion.';
    } else if (error.code === 'ECONNREFUSED') {
      console.error('Connexion refusée lors de la suppression');
      errorMessage = 'Connexion impossible : Vérifiez que le serveur est démarré.';
    } else if (error.message) {
      // Erreur lors de la configuration de la requête
      console.error('Erreur de configuration de la requête de suppression:', error.message);
      errorMessage = `Erreur technique: ${error.message}`;
    }
    
    console.error('Message d\'erreur affiché à l\'utilisateur:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const getOrderById = async (id: string | number): Promise<Order | null> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    
    console.log('Tentative de récupération de la commande:', id);
    console.log('URL appelée:', api.defaults.baseURL + `/orders/${id}`);
    
    const response = await api.get(`/orders/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.data) {
      throw new Error('Aucune donnée reçue du serveur');
    }
    const order = response.data?.data?.order || response.data?.data || response.data;
    return mapOrderFromBackend(order);
  } catch (error: any) {
    let errorMessage = 'Impossible de charger la commande.';
    if (error.response) {
      if (error.response.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        window.location.href = '/login';
      } else if (error.response.status === 404) {
        errorMessage = 'Commande introuvable.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    }
    throw new Error(errorMessage);
  }
};

export const updateOrder = async (id: number, orderData: Partial<Order>): Promise<Order | null> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    
    console.log('Tentative de mise à jour de la commande:', id, 'avec les données:', orderData);
    
    // Convertir les données au format attendu par le backend (snake_case)
    const backendData: any = {
      status: orderData.status,
      notes: orderData.notes,
      shipping_address: orderData.shippingAddress,
      payment_method: orderData.paymentMethod
    };
    
    // Convertir les items si présents
    if (orderData.items && Array.isArray(orderData.items)) {
      backendData.items = orderData.items.map((item: any) => ({
        id: item.id,
        product_id: item.productId || item.product_id,
        quantity: item.quantity,
        unit_price: item.unitPrice || item.unit_price
      }));
    }
    
    console.log('Données envoyées au backend:', backendData);
    
    const response = await api.put(`/orders/${id}`, backendData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Réponse de mise à jour:', response.status, response.data);
    const updatedOrder = response.data.data || response.data;
    return mapOrderFromBackend(updatedOrder);
    
  } catch (error: any) {
    console.error('Erreur lors de la mise à jour de la commande:', error);
    
    let errorMessage = 'Erreur lors de la mise à jour';
    
    if (error.response) {
      if (error.response.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        window.location.href = '/login';
      } else if (error.response.status === 404) {
        errorMessage = 'Commande introuvable.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      errorMessage = 'Le serveur ne répond pas. Vérifiez votre connexion.';
    } else if (error.message) {
      errorMessage = `Erreur technique: ${error.message}`;
    }
    
    throw new Error(errorMessage);
  }
};

export const updatePaymentStatus = async (id: number, paymentStatus: string): Promise<Order | null> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    
    console.log(`Tentative de mise à jour du statut de paiement de la commande ${id} vers:`, paymentStatus);
    
    const response = await api.patch(`/orders/${id}/payment-status`, 
      { paymentStatus },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Réponse de mise à jour du statut de paiement:', response.status, response.data);
    const updatedOrder = response.data.data || response.data;
    return mapOrderFromBackend(updatedOrder);
    
  } catch (error: any) {
    console.error('Erreur lors de la mise à jour du statut de paiement:', error);
    
    let errorMessage = 'Erreur lors de la mise à jour du statut de paiement';
    
    if (error.response) {
      if (error.response.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        window.location.href = '/login';
      } else if (error.response.status === 403) {
        errorMessage = 'Accès refusé : Vous n\'avez pas les droits nécessaires.';
      } else if (error.response.status === 404) {
        errorMessage = 'Commande introuvable.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      errorMessage = 'Le serveur ne répond pas. Vérifiez votre connexion.';
    } else if (error.message) {
      errorMessage = `Erreur technique: ${error.message}`;
    }
    
    throw new Error(errorMessage);
  }
};

const orderService = {
  getOrders,
  deleteOrder,
  getOrderById,
  updateOrder,
  updatePaymentStatus
};

export default orderService;